import { useLayoutEffect } from "react";

// Absolute Basis-URL der Produktionsseite. Canonical-Links, og:url und die
// Sitemap müssen absolut sein – relative Angaben ignoriert Google.
// Bei einem Domainwechsel reicht es, diesen Wert und SITE_URL in
// scripts/generate-sitemap.mjs anzupassen.
export const SITE_URL = "https://merdovic-trockenbau.de";

export const absoluteUrl = (path = "/") =>
  `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;

// Ein <meta>/<link> anlegen oder aktualisieren. Die Tags bleiben zwischen den
// Routen bestehen und werden überschrieben, statt sie zu entfernen und neu zu
// erzeugen – so gibt es nie einen Moment ohne Description oder Canonical.
const upsert = (tagName, selector, attributes) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(tagName);
    document.head.appendChild(el);
  }
  Object.entries(attributes).forEach(([key, value]) => el.setAttribute(key, value));
};

const setMeta = (name, content) =>
  upsert("meta", `meta[name="${name}"]`, { name, content });

const setProperty = (property, content) =>
  upsert("meta", `meta[property="${property}"]`, { property, content });

const setLink = (rel, href) => upsert("link", `link[rel="${rel}"]`, { rel, href });

// Seitenspezifisches strukturiertes Datum (z. B. Breadcrumbs). Das globale
// LocalBusiness-Schema steht fest in public/index.html und bleibt unberührt.
const SCHEMA_ID = "seo-page-schema";

const setSchema = (schema) => {
  const existing = document.getElementById(SCHEMA_ID);
  if (!schema) {
    if (existing) existing.remove();
    return;
  }
  const el = existing || document.createElement("script");
  el.id = SCHEMA_ID;
  el.type = "application/ld+json";
  el.textContent = JSON.stringify(schema);
  if (!existing) document.head.appendChild(el);
};

/**
 * Setzt Titel, Description, Canonical und Social-Tags für die aktuelle Route.
 *
 * Wird bewusst per useLayoutEffect geschrieben: das Prerendering
 * (scripts/prerender.mjs) serialisiert den <head> nach dem ersten Rendern,
 * und useLayoutEffect läuft davor – der ausgelieferte statische HTML enthält
 * dadurch bereits die richtigen Tags, ohne dass ein Crawler JavaScript
 * ausführen muss.
 */
export const Seo = ({
  title,
  description,
  path = "/",
  // Eigenes Vorschaubild im von Facebook, LinkedIn und WhatsApp erwarteten
  // Format 1200x630. Der Hero (1920x1280) wäre in der Vorschau beschnitten,
  // und die in index.html angegebenen Maße würden nicht mehr stimmen.
  image = "/img/og-image.jpg",
  noindex = false,
  schema = null,
}) => {
  useLayoutEffect(() => {
    const url = absoluteUrl(path);
    const imageUrl = image.startsWith("http") ? image : absoluteUrl(image);

    document.title = title;
    setMeta("description", description);
    setMeta("robots", noindex ? "noindex, follow" : "index, follow");
    setLink("canonical", url);

    setProperty("og:title", title);
    setProperty("og:description", description);
    setProperty("og:url", url);
    setProperty("og:image", imageUrl);

    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", imageUrl);

    setSchema(schema);
  }, [title, description, path, image, noindex, schema]);

  return null;
};

// Breadcrumbs für Unterseiten. Google zeigt sie in den Suchergebnissen statt
// der nackten URL an und versteht die Seitenhierarchie besser.
export const breadcrumbSchema = (name, path) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Startseite",
      item: absoluteUrl("/"),
    },
    {
      "@type": "ListItem",
      position: 2,
      name,
      item: absoluteUrl(path),
    },
  ],
});
